import { format } from "date-fns";
import intl from "react-intl-universal";
import {
  downloadCsv,
  endOfDay,
  formatFilterAmount,
  formatFilterDate,
  labelById,
  parseAmountInput,
  startOfDay,
  toggleFilterListValue,
} from "../../../Components/Molecules/GridHistory/gridFilterUtils";
import { getHistoryKindMeta, TRANSFER_METHODS } from "./constants";

export { toggleFilterListValue };

export const EMPTY_CONTA_INTERNACIONAL_HISTORY_FILTERS = {
  dateFrom: null,
  dateTo: null,
  amountMin: "",
  amountMax: "",
  currencies: [],
  statuses: [],
  types: [],
  paymentMethods: [],
  searchQuery: "",
};

export const CI_HISTORY_CURRENCIES = [
  { id: "usd", label: "USD" },
  { id: "eur", label: "EUR" },
  { id: "pyg", label: "PYG" },
  { id: "brl", label: "BRL" },
];

export const getCiHistoryStatuses = () => [
  { id: "concluida", label: intl.get("contaInternacional.statusCompleted") },
  { id: "processando", label: intl.get("contaInternacional.statusProcessing") },
];

/** @deprecated Use getCiHistoryStatuses() at render time for i18n */
export const CI_HISTORY_STATUSES = [
  { id: "concluida", label: "Concluída" },
  { id: "processando", label: "Em processamento" },
];

export const getCiHistoryTypes = () =>
  Object.entries(getHistoryKindMeta()).map(([id, meta]) => ({
    id,
    label: meta.label,
  }));

/** International rails only — Pix is domestic Extrato, not Conta Internacional ledger. */
const CI_HISTORY_PAYMENT_METHOD_IDS = ["wire", "ach", "sepa", "swift"];

export const getCiPaymentMethodOptions = () =>
  CI_HISTORY_PAYMENT_METHOD_IDS.map((id) => ({
    id,
    label: TRANSFER_METHODS[id]?.label || id.toUpperCase(),
  }));

export const resolveCiPaymentMethodId = (method = "") => {
  const lower = String(method).toLowerCase();
  if (lower.includes("wire")) return "wire";
  if (lower.includes("ach")) return "ach";
  if (lower.includes("sepa")) return "sepa";
  if (lower.includes("swift")) return "swift";
  if (lower === "fx" || lower.includes("efx") || /\bfx\b/.test(lower)) return null;
  return null;
};

export const getHistoryItemPaymentMethodId = (item) => {
  if (!item?.method) return null;
  return resolveCiPaymentMethodId(item.method);
};

/** @deprecated Use getCiHistoryTypes() at render time for i18n */
export const CI_HISTORY_TYPES = Object.entries(getHistoryKindMeta()).map(([id, meta]) => ({
  id,
  label: meta.label,
}));

const parseHistoryDate = (dateStr) => {
  const [datePart, timePart = "00:00"] = dateStr.split(" ");
  const [day, month, year] = datePart.split("/").map(Number);
  const [hours, minutes] = timePart.split(":").map(Number);
  return new Date(year, month - 1, day, hours, minutes);
};

const normalizeStatus = (status) => {
  const value = String(status || "").toLowerCase();
  if (value.includes("process")) return "processando";
  return "concluida";
};

export const getHistoryItemCurrencies = (item) => {
  const currencies = new Set();

  switch (item.historyKind) {
    case "purchases":
    case "withdrawals":
    case "transfers":
      currencies.add(item.asset);
      break;
    case "conversions":
      currencies.add(item.from);
      currencies.add(item.to);
      break;
    default:
      break;
  }

  return [...currencies];
};

export const getHistoryItemAmountBrl = (item) => {
  switch (item.historyKind) {
    case "purchases":
      return Number(item.amountBrl) || 0;
    case "withdrawals":
    case "transfers":
      return Number(item.amount) || 0;
    case "conversions":
      return Number(item.amountTo) || 0;
    default:
      return 0;
  }
};

export const hasActiveContaInternacionalHistoryFilters = (filters) =>
  Boolean(filters.dateFrom)
  || Boolean(filters.dateTo)
  || filters.amountMin !== ""
  || filters.amountMax !== ""
  || filters.currencies.length > 0
  || filters.statuses.length > 0
  || filters.types.length > 0
  || filters.paymentMethods.length > 0
  || Boolean(filters.searchQuery?.trim());

export const applyContaInternacionalHistoryFilters = (items, filters) => {
  const minAmount = parseAmountInput(filters.amountMin);
  const maxAmount = parseAmountInput(filters.amountMax);
  const dateFrom = filters.dateFrom ? startOfDay(filters.dateFrom) : null;
  const dateTo = filters.dateTo ? endOfDay(filters.dateTo) : null;

  return items.filter((item) => {
    const itemDate = parseHistoryDate(item.date);

    if (dateFrom && itemDate < dateFrom) return false;
    if (dateTo && itemDate > dateTo) return false;

    const amount = getHistoryItemAmountBrl(item);
    if (minAmount !== null && amount < minAmount) return false;
    if (maxAmount !== null && amount > maxAmount) return false;

    if (filters.currencies.length > 0) {
      const itemCurrencies = getHistoryItemCurrencies(item);
      const matchesCurrency = filters.currencies.some((currency) =>
        itemCurrencies.includes(currency),
      );
      if (!matchesCurrency) return false;
    }

    if (filters.statuses.length > 0) {
      const itemStatus = normalizeStatus(item.status);
      if (!filters.statuses.includes(itemStatus)) return false;
    }

    if (filters.types.length > 0 && !filters.types.includes(item.historyKind)) {
      return false;
    }

    if (filters.paymentMethods.length > 0) {
      const paymentMethodId = getHistoryItemPaymentMethodId(item);
      if (!paymentMethodId || !filters.paymentMethods.includes(paymentMethodId)) return false;
    }

    if (filters.searchQuery?.trim()) {
      const query = filters.searchQuery.trim().toLowerCase();
      const haystack = [
        item.id,
        item.date,
        item.status,
        item.historyKind,
        item.asset,
        item.from,
        item.to,
        item.destination,
        item.recipient,
        item.method,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      if (!haystack.includes(query)) return false;
    }

    return true;
  });
};

export const buildContaInternacionalHistoryFilterTags = (filters) => {
  const tags = [];

  if (filters.searchQuery?.trim()) {
    tags.push({
      key: "searchQuery",
      iconType: "search",
      value: filters.searchQuery.trim(),
    });
  }

  if (filters.dateFrom) {
    tags.push({
      key: "dateFrom",
      iconType: "date",
      value: formatFilterDate(filters.dateFrom),
    });
  }

  if (filters.dateTo) {
    tags.push({
      key: "dateTo",
      iconType: "date",
      value: formatFilterDate(filters.dateTo),
    });
  }

  if (filters.amountMin !== "") {
    tags.push({
      key: "amountMin",
      iconType: "amount",
      value: formatFilterAmount(filters.amountMin),
    });
  }

  if (filters.amountMax !== "") {
    tags.push({
      key: "amountMax",
      iconType: "amount",
      value: formatFilterAmount(filters.amountMax),
    });
  }

  if (filters.currencies.length > 0) {
    tags.push({
      key: "currencies",
      iconType: "currency",
      value: filters.currencies
        .map((currency) => labelById(CI_HISTORY_CURRENCIES, currency))
        .join(", "),
    });
  }

  if (filters.statuses.length > 0) {
    tags.push({
      key: "statuses",
      iconType: "status",
      value: filters.statuses
        .map((status) => labelById(getCiHistoryStatuses(), status))
        .join(", "),
    });
  }

  if (filters.types.length > 0) {
    tags.push({
      key: "types",
      iconType: "type",
      value: filters.types
        .map((type) => labelById(getCiHistoryTypes(), type))
        .join(", "),
    });
  }

  if (filters.paymentMethods.length > 0) {
    tags.push({
      key: "paymentMethods",
      iconType: "payment",
      value: filters.paymentMethods
        .map((method) => labelById(getCiPaymentMethodOptions(), method))
        .join(", "),
    });
  }

  return tags;
};

export const removeContaInternacionalHistoryFilterTag = (filters, tagKey) => {
  switch (tagKey) {
    case "dateFrom":
      return { ...filters, dateFrom: null };
    case "dateTo":
      return { ...filters, dateTo: null };
    case "amountMin":
      return { ...filters, amountMin: "" };
    case "amountMax":
      return { ...filters, amountMax: "" };
    case "currencies":
      return { ...filters, currencies: [] };
    case "statuses":
      return { ...filters, statuses: [] };
    case "types":
      return { ...filters, types: [] };
    case "paymentMethods":
      return { ...filters, paymentMethods: [] };
    case "searchQuery":
      return { ...filters, searchQuery: "" };
    default:
      return filters;
  }
};

export const isContaInternacionalChipFilterActive = (filters, chipId) => {
  switch (chipId) {
    case "date":
      return Boolean(filters.dateFrom || filters.dateTo);
    case "amount":
      return filters.amountMin !== "" || filters.amountMax !== "";
    case "currency":
      return filters.currencies.length > 0;
    case "status":
      return filters.statuses.length > 0;
    case "type":
      return filters.types.length > 0;
    case "payment":
      return filters.paymentMethods.length > 0;
    case "more":
      return filters.currencies.length > 0
        || filters.statuses.length > 0
        || filters.paymentMethods.length > 0;
    default:
      return false;
  }
};

const getHistoryKindLabel = (historyKind) =>
  labelById(getCiHistoryTypes(), historyKind);

export const exportContaInternacionalHistoryCsv = (items) => {
  const headers = ["Data", "Tipo", "Descrição", "Valor", "Status", "Moeda"];

  const rows = items.map((item) => {
    const currencies = getHistoryItemCurrencies(item).join(" / ").toUpperCase();
    let description = item.id;

    switch (item.historyKind) {
      case "purchases":
        description = `Compra ${item.asset?.toUpperCase()}`;
        break;
      case "withdrawals":
        description = item.destination || "Saque";
        break;
      case "transfers":
        description = item.recipient || "Envio";
        break;
      case "conversions":
        description = `${item.from?.toUpperCase()} → ${item.to?.toUpperCase()}`;
        break;
      default:
        break;
    }

    return [
      item.date,
      getHistoryKindLabel(item.historyKind),
      description,
      getHistoryItemAmountBrl(item).toFixed(2).replace(".", ","),
      item.status,
      currencies,
    ];
  });

  downloadCsv(
    headers,
    rows,
    `historico-conta-internacional-${format(new Date(), "yyyy-MM-dd")}.csv`,
  );
};
